<div class="drt-table-section" *ngIf="hasRenderableData()">
    <div class="drt-table-title" [attr.aria-expanded]="isExpanded">
        <span>{{ tableTitle }}</span>

        <div class="drt-title-actions">
            <button
                type="button"
                class="drt-icon-btn"
                title="Download Excel"
                aria-label="Download DRT Excel file"
                (click)="downloadAsExcel()">
                <span class="drt-toggle-icon">download</span>
            </button>

            <button
                *ngIf="isExpanded"
                type="button"
                class="drt-icon-btn"
                title="Maximize"
                aria-label="Maximize DRT table"
                (click)="openMaximizedView($event)">
                <span class="drt-toggle-icon">open_in_full</span>
            </button>

            <button
                type="button"
                class="drt-icon-btn"
                [title]="isExpanded ? 'Collapse' : 'Expand'"
                [attr.aria-label]="isExpanded ? 'Collapse DRT table' : 'Expand DRT table'"
                (click)="toggleExpanded()">
                <span class="drt-toggle-icon">{{ isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</span>
            </button>
        </div>
    </div>

    <!-- ── Single DRT result (dynamicColumns=true): vertical layout ────── -->
    <ng-container *ngIf="dynamicColumns">
        <div class="drt-table-scroll drt-vertical-scroll" *ngIf="isExpanded" role="region" aria-label="DRT parameters table" tabindex="0">
            <table class="drt-table drt-table-vertical">
                <colgroup>
                    <col class="drt-col-section">
                    <col class="drt-col-sub">
                    <col class="drt-col-field">
                    <col class="drt-col-value">
                    <col class="drt-col-citation">
                    <col class="drt-col-ai">
                    <col class="drt-col-conflict">
                    <col class="drt-col-ewo">
                </colgroup>
                <thead>
                    <tr>
                        <th class="drt-vh drt-vh-section">Section</th>
                        <th class="drt-vh drt-vh-sub">Sub-section</th>
                        <th class="drt-vh drt-vh-field">Field</th>
                        <th class="drt-vh drt-vh-value">Value</th>
                        <th class="drt-vh drt-vh-citation">Citation</th>
                        <th class="drt-vh drt-vh-ai">AI Reasoning</th>
                        <th class="drt-vh drt-vh-conflict">Conflicts</th>
                        <th class="drt-vh drt-vh-ewo">Past EWO Suggestions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let vrow of verticalRows; let i = index"
                        [class.drt-vrow-alt]="i % 2 === 1">
                        <td *ngIf="vrow.showSection"
                            class="drt-vc drt-vc-section"
                            [attr.rowspan]="vrow.sectionRowspan">{{ vrow.section }}</td>
                        <td *ngIf="vrow.showSubSection"
                            class="drt-vc drt-vc-sub"
                            [attr.rowspan]="vrow.subSectionRowspan">{{ vrow.subSection }}</td>
                        <td class="drt-vc drt-vc-field">{{ vrow.fieldName }}</td>
                        <td class="drt-vc drt-vc-value">
                            <div class="drt-value-main">{{ vrow.value }}</div>
                        </td>
                        <td class="drt-vc drt-vc-citation">
                            <ng-container *ngFor="let citation of vrow.citations; let last = last">
                                <a
                                    *ngIf="isRealCitationUrl(citation.url); else plainCitationV1"
                                    class="drt-citation-link"
                                    [href]="citation.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    (mousedown)="openCitation(citation.url, $event)"
                                    (keydown)="onCitationKeydown(citation.url, $event)"
                                    (click)="$event.preventDefault(); $event.stopPropagation()">{{ formatCitationLabel(citation.label, citation.url) }}</a>
                                <ng-template #plainCitationV1><span class="drt-citation-text">{{ formatCitationLabel(citation.label, citation.url) }}</span></ng-template>
                                <br *ngIf="!last">
                            </ng-container>
                        </td>
                        <td class="drt-vc drt-vc-ai">{{ vrow.aiReason }}</td>
                        <td class="drt-vc drt-vc-conflict">
                            <span class="drt-conflict-text" *ngIf="vrow.conflict">{{ vrow.conflict }}</span>
                        </td>
                        <td class="drt-vc drt-vc-ewo">
                            <ng-container *ngFor="let suggestion of vrow.pastEwoSuggestions; let lastSugg = last">
                                <span class="drt-ewo-value">{{ suggestion.value }}</span>
                                <ng-container *ngFor="let doc of suggestion.documents; let lastDoc = last">
                                    <a
                                        class="drt-citation-link"
                                        [href]="doc.url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        (mousedown)="openCitation(doc.url, $event)"
                                        (keydown)="onCitationKeydown(doc.url, $event)"
                                        (click)="$event.preventDefault(); $event.stopPropagation()">{{ formatCitationLabel(doc.label, doc.url) }}</a>
                                    <br *ngIf="!lastDoc">
                                </ng-container>
                                <br *ngIf="!lastSugg">
                            </ng-container>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </ng-container>

    <!-- ── Comparison DRT (dynamicColumns=false): original horizontal ──── -->
    <ng-container *ngIf="!dynamicColumns">
        <div class="drt-table-scroll" *ngIf="isExpanded" role="region" aria-label="DRT parameters table" tabindex="0">
            <table class="drt-table">
                <thead>
                    <tr>
                        <th class="drt-doc-column drt-header-top">DRT</th>
                        <th *ngFor="let group of topHeaderGroups"
                            [attr.colspan]="group.span"
                            class="drt-header-top">{{ group.label }}</th>
                    </tr>
                    <tr>
                        <th class="drt-doc-column drt-header-sub"></th>
                        <th *ngFor="let group of subHeaderGroups"
                            [attr.colspan]="group.span"
                            class="drt-header-sub">{{ group.label }}</th>
                    </tr>
                    <tr>
                        <th class="drt-doc-column drt-header-leaf"></th>
                        <th *ngFor="let col of columns" class="drt-header-leaf">{{ col.leafLabel }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let row of rows">
                        <td class="drt-doc-column drt-doc-row-title">{{ row.documentTitle }}</td>
                        <td *ngFor="let val of row.values" class="drt-value-cell">{{ val }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </ng-container>
</div>

<div class="drt-modal-overlay" *ngIf="isModalOpen" (click)="closeMaximizedView()"></div>

<div class="drt-modal" *ngIf="isModalOpen" role="dialog" aria-modal="true" aria-label="Maximized DRT table">
    <div class="drt-modal-header">
        <span class="drt-modal-title">{{ tableTitle }}</span>
        <div class="drt-title-actions">
            <button
                type="button"
                class="drt-icon-btn"
                title="Download Excel"
                aria-label="Download DRT table as Excel"
                (click)="downloadAsExcel()">
                <span class="drt-toggle-icon">download</span>
            </button>
            <button
                type="button"
                class="drt-icon-btn"
                title="Minimize"
                aria-label="Minimize DRT table"
                (click)="closeMaximizedView()">
                <span class="drt-toggle-icon">close_fullscreen</span>
            </button>
        </div>
    </div>

    <!-- ── Single DRT modal (dynamicColumns=true): vertical layout ─────── -->
    <ng-container *ngIf="dynamicColumns">
        <div class="drt-modal-table-scroll drt-vertical-scroll" role="region" aria-label="Maximized DRT parameters table" tabindex="0">
            <table class="drt-table drt-table-vertical drt-table-full">
                <colgroup>
                    <col class="drt-col-section">
                    <col class="drt-col-sub">
                    <col class="drt-col-field">
                    <col class="drt-col-value">
                    <col class="drt-col-citation">
                    <col class="drt-col-ai">
                    <col class="drt-col-conflict">
                    <col class="drt-col-ewo">
                </colgroup>
                <thead>
                    <tr>
                        <th class="drt-vh drt-vh-section">Section</th>
                        <th class="drt-vh drt-vh-sub">Sub-section</th>
                        <th class="drt-vh drt-vh-field">Field</th>
                        <th class="drt-vh drt-vh-value">Value</th>
                        <th class="drt-vh drt-vh-citation">Citation</th>
                        <th class="drt-vh drt-vh-ai">AI Reasoning</th>
                        <th class="drt-vh drt-vh-conflict">Conflicts</th>
                        <th class="drt-vh drt-vh-ewo">Past EWO Suggestions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let vrow of allVerticalRows; let i = index"
                        [class.drt-vrow-alt]="i % 2 === 1">
                        <td *ngIf="vrow.showSection"
                            class="drt-vc drt-vc-section"
                            [attr.rowspan]="vrow.sectionRowspan">{{ vrow.section }}</td>
                        <td *ngIf="vrow.showSubSection"
                            class="drt-vc drt-vc-sub"
                            [attr.rowspan]="vrow.subSectionRowspan">{{ vrow.subSection }}</td>
                        <td class="drt-vc drt-vc-field">{{ vrow.fieldName }}</td>
                        <td class="drt-vc drt-vc-value">
                            <div class="drt-value-main">{{ vrow.value }}</div>
                        </td>
                        <td class="drt-vc drt-vc-citation">
                            <ng-container *ngFor="let citation of vrow.citations; let last = last">
                                <a
                                    *ngIf="isRealCitationUrl(citation.url); else plainCitationV2"
                                    class="drt-citation-link"
                                    [href]="citation.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    (mousedown)="openCitation(citation.url, $event)"
                                    (keydown)="onCitationKeydown(citation.url, $event)"
                                    (click)="$event.preventDefault(); $event.stopPropagation()">{{ formatCitationLabel(citation.label, citation.url) }}</a>
                                <ng-template #plainCitationV2><span class="drt-citation-text">{{ formatCitationLabel(citation.label, citation.url) }}</span></ng-template>
                                <br *ngIf="!last">
                            </ng-container>
                        </td>
                        <td class="drt-vc drt-vc-ai">{{ vrow.aiReason }}</td>
                        <td class="drt-vc drt-vc-conflict">
                            <span class="drt-conflict-text" *ngIf="vrow.conflict">{{ vrow.conflict }}</span>
                        </td>
                        <td class="drt-vc drt-vc-ewo">
                            <ng-container *ngFor="let suggestion of vrow.pastEwoSuggestions; let lastSugg = last">
                                <span class="drt-ewo-value">{{ suggestion.value }}</span>
                                <ng-container *ngFor="let doc of suggestion.documents; let lastDoc = last">
                                    <a
                                        class="drt-citation-link"
                                        [href]="doc.url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        (mousedown)="openCitation(doc.url, $event)"
                                        (keydown)="onCitationKeydown(doc.url, $event)"
                                        (click)="$event.preventDefault(); $event.stopPropagation()">{{ formatCitationLabel(doc.label, doc.url) }}</a>
                                    <br *ngIf="!lastDoc">
                                </ng-container>
                                <br *ngIf="!lastSugg">
                            </ng-container>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </ng-container>

    <!-- ── Comparison DRT modal (dynamicColumns=false): original horizontal -->
    <ng-container *ngIf="!dynamicColumns">
        <div class="drt-modal-table-scroll" role="region" aria-label="Maximized DRT parameters table" tabindex="0">
            <table class="drt-table drt-table-full">
                <thead>
                    <tr>
                        <th class="drt-doc-column drt-header-top">DRT</th>
                        <th *ngFor="let group of allTopHeaderGroups"
                            [attr.colspan]="group.span"
                            class="drt-header-top">{{ group.label }}</th>
                    </tr>
                    <tr>
                        <th class="drt-doc-column drt-header-sub"></th>
                        <th *ngFor="let group of allSubHeaderGroups"
                            [attr.colspan]="group.span"
                            class="drt-header-sub">{{ group.label }}</th>
                    </tr>
                    <tr>
                        <th class="drt-doc-column drt-header-leaf"></th>
                        <th *ngFor="let col of allColumns" class="drt-header-leaf">{{ col.leafLabel }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let row of allRows">
                        <td class="drt-doc-column drt-doc-row-title">{{ row.documentTitle }}</td>
                        <td *ngFor="let val of row.values" class="drt-value-cell">{{ val }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </ng-container>
</div>
